import React from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  Box,
  Avatar,
  Link,
  Stack,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const DEFAULT_IMAGE =
  "https://www.pay4freight.com/wp-content/uploads/2022/06/Benefits-of-a-Transport-Management-System-for-Owner-Operators.png";

const BlogCard = ({ post }) => {
  const excerpt = post.content.replace(/<[^>]+>/g, "").slice(0, 40) + "…";

  const thumbnail = post.featureImage?.[0]?.url || DEFAULT_IMAGE;

  return (
    <Link
      href={`/blog-detail/${post.slug}`}
      underline="none"
      sx={{ display: "block" }}
    >
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.3s, box-shadow 0.3s",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          boxShadow: (theme) => theme.shadows[2],
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: (theme) => theme.shadows[6],
          },
          bgcolor: "background.paper",
        }}
      >
        {/* Featured icon */}
        {post.isFeatured && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              bgcolor: "white",
              borderRadius: "50%",
              zIndex: 1,
            }}
          >
            <StarIcon sx={{ color: "crimson", fontSize: 20 }} />
          </Box>
        )}

        {/* Title */}
        <CardContent sx={{ pb: 0 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{
              lineHeight: 1.5,
              mb: 2,
              fontFamily: '"Roboto", sans-serif',
              letterSpacing: 0.5,
              textTransform: "capitalize",
            }}
          >
            {`${post.title.replace(/<[^>]+>/g, "").slice(0, 35)}...`}
          </Typography>
        </CardContent>

        {/* Thumbnail */}
        <CardMedia
          component="img"
          image={thumbnail}
          alt={post.title}
          sx={{
            height: 200,
            width: "100%",
            objectFit: "cover",
          }}
        />

        {/* Content */}
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.95rem",
              color: "text.primary",
              fontFamily: '"Quicksand", sans-serif',
            }}
          >
            {excerpt}
          </Typography>
        </CardContent>

        {/* Chips Below Image */}
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="flex-end"
          sx={{ mr: 2, mb: 2 }}
        >
          {post.category?.name && (
            <Chip
              label={post.category.name}
              size="small"
              sx={{
                bgcolor: "crimson",
                color: "white",
                textTransform: "capitalize",
                fontSize: "0.75rem",
                fontWeight: 500,
                fontFamily: '"Quicksand", sans-serif',
              }}
            />
          )}
        </Stack>

        {/* Author and Date at Bottom */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#f9f9f9",
            p: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box display="flex" alignItems="center">
            {/* Placeholder for Author Image */}
            <Avatar
              src={post.image?.[0]?.url}
              alt={post.author}
              sx={{ width: 40, height: 40, mr: 1, border: '1px solid crimson' }}
            />

            {/* Author Name and Date */}
            <Stack spacing={0.5}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ fontFamily: '"Quicksand", sans-serif' }}
              >
                {post.author}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: '"Quicksand", sans-serif' }}
              >
                {formatDate(post.createdAt)}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Card>
    </Link>
  );
};

export default BlogCard;
